// src/pages/Services/tradeInService.ts
// Public write-only service for the "Sell / Trade-In Your Car" page.
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../Firebase/Config';

export interface TradeInSubmission {
  ownerName: string;
  ownerPhone: string;
  ownerEmail?: string;
  carMake: string;
  carModel: string;
  carYear: number;
  mileageKm?: number;
  condition?: string;
  registrationNumber?: string;
  askingPrice?: number;
  description?: string;
  // Set when the customer is signed in at submission time — lets "My
  // Account" show this submission in their activity history.
  submittedByUid?: string;
}

export const submitTradeIn = async (data: TradeInSubmission, imageFiles?: File[]): Promise<string> => {
  let images: string[] = [];

  if (imageFiles && imageFiles.length > 0) {
    images = await Promise.all(
      imageFiles.map(async (file) => {
        const imageRef = ref(storage, `tradeIns/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(imageRef, file);
        return getDownloadURL(snapshot.ref);
      })
    );
  }

  const ref_ = await addDoc(collection(db, 'tradeInSubmissions'), {
    ...data,
    images,
    status: 'New Submission',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref_.id;
};

export interface TradeInRecord extends TradeInSubmission {
  id: string;
  status: string;
  images: string[];
  createdAt?: Timestamp;
}

// Used by "My Account" to show a signed-in customer's own past trade-in
// submissions (only ones submitted while signed in are tagged with
// submittedByUid, so older anonymous submissions won't appear).
export const fetchMyTradeIns = async (uid: string): Promise<TradeInRecord[]> => {
  const q = query(collection(db, 'tradeInSubmissions'), where('submittedByUid', '==', uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as TradeInSubmission & { status: string; images: string[]; createdAt?: Timestamp }) }));
};
